import moment from 'moment-timezone';
import { Measure, MetricDataPoint, Periods } from 'cio/utils/metrics/common';
import { isNone } from '@ember/utils';
import merge from 'lodash.merge';
import { Grouping } from './metrics/resolution';
import type {
  AxisLabelsFormatterContextObject,
  Options,
  Point,
  PointOptionsObject,
  Series,
  SeriesTooltipOptionsObject,
  TooltipFormatterContextObject,
} from 'highcharts';
import { MeasureType } from './metrics/measure';
import { JourneyMetricsMeasure } from './journey-metrics';

export const DEFAULT_Y_TICKS = 10;
export const FORMAT_THRESHOLD = 1000000;

// apparently "ddd" is misspelled 😂
// eslint-disable-next-line spellcheck/spell-checker
const TOOLTIP_WEEK_MOMENT_FORMAT = 'ddd, MMM D, Y';

const MARKER_SETTINGS = Object.freeze({
  marker: {
    enabled: false,
    states: {
      hover: {
        radius: 3,
      },
    },
  },
  states: {
    hover: {
      halo: {
        size: 6,
      },
    },
  },
});

const DASHED_EDGE_POINT_MARKER_OPTIONS = {
  enabled: true,
  symbol: 'circle',
  radius: 3,
};

export interface CioHighchartsPointOptionsObject extends PointOptionsObject {
  custom: {
    originalY: null | number;
    isNull: boolean;
    measureType: MeasureType;
    denominator: Measure | JourneyMetricsMeasure;
  };
}

export interface CioHighchartsPoint extends Point {
  options: CioHighchartsPointOptionsObject;
}

// Mutates a chart's options and series to:
// - make a line graph's section dashed when grouped in weekly/monthly bins and
// a given week/month is incomplete
// - add a dot marker at the edge of those dashed lines for another visual
// marker on where incomplete data starts/ends
export function addDashedZonesToIncompleteDataPoints(
  options: Options,
  timestamps: number[],
  timeRange: { start: moment.Moment; end: moment.Moment },
  grouping: Grouping,
) {
  let optionsCopy: Options = merge({}, options);

  // First we reset all series data points to disable markers. Otherwise
  // Highcharts might keep old markers around when settings change
  optionsCopy.series = (optionsCopy.series ?? []).map((s) => ({
    ...s,
    data: (s as any).data.map((p: PointOptionsObject) => ({ ...p, marker: null })),
  }));

  // We also reset zones, because they may suffer from the same update problem
  // as the above series

  optionsCopy.plotOptions = merge({}, optionsCopy.plotOptions ?? {}, {
    series: {
      zones: [],
    },
  });

  if (grouping === Grouping.Weekly) {
    let diffInDays = timeRange.end.diff(timeRange.start, 'days') + 1;
    if (diffInDays % 7 !== 0) {
      let plotOptions = {
        series: {
          zoneAxis: 'x',
          zones: [
            {
              value: timestamps[timestamps.length - 2],
            },
            {
              className: 'highcharts-dashed-line',
            },
          ],
        },
      };

      optionsCopy.plotOptions = merge({}, optionsCopy.plotOptions ?? {}, plotOptions);

      // Add dot marker to second-to-last data point (the one that starts the
      // dashed line)
      optionsCopy.series = optionsCopy.series.map((s: any) => ({
        ...s,
        data: s.data.map((p: PointOptionsObject, i: number) => {
          let point = { ...p };

          if (i === s.data.length - 2) {
            point.marker = { ...DASHED_EDGE_POINT_MARKER_OPTIONS };
          }

          return point;
        }),
      }));
    }
  } else if (grouping === Grouping.Monthly) {
    let hasFullStartMonth = timeRange.start.date() === moment(timeRange.start).startOf('month').date();
    let hasFullEndMonth = timeRange.end.date() === moment(timeRange.end).endOf('month').date();

    let zones = [];

    if (!hasFullStartMonth) {
      zones.push({
        className: 'highcharts-dashed-line',
        value: timestamps[1],
      });
    }

    if (!hasFullEndMonth) {
      zones.push(
        {
          value: timestamps[timestamps.length - 2],
        },
        {
          className: 'highcharts-dashed-line',
        },
      );
    }

    let plotOptions = {
      series: {
        zoneAxis: 'x',
        zones,
      },
    };
    optionsCopy.plotOptions = merge({}, optionsCopy.plotOptions ?? {}, plotOptions);

    optionsCopy.series = optionsCopy.series.map((s: any) => ({
      ...s,
      data: s.data.map((p: PointOptionsObject, i: number) => {
        let point = { ...p };

        // Add a dot marker to the second point, if there's a dashed line
        // before it
        if (!hasFullStartMonth && i === 1) {
          point.marker = { ...DASHED_EDGE_POINT_MARKER_OPTIONS };
        }

        // Add a dot marker to the second-to-last point, if there's a dashed
        // line after it
        if (!hasFullEndMonth && i === s.data.length - 2) {
          point.marker = { ...DASHED_EDGE_POINT_MARKER_OPTIONS };
        }

        return point;
      }),
    }));
  }

  return optionsCopy;
}

export function mapPercentDataPoints(data: MetricDataPoint[]) {
  return data.map((p) => ({
    ...p,
    y: p.y == null ? null : p.y * 100,
  }));
}

// Be careful changing these settings, as they will apply to all charts within
// the app (unless they're overridden in a given scenario, of course)
export const DEFAULT_CHART_OPTIONS = Object.freeze({
  chart: {
    height: 500,
    zoomType: 'x',
    styledMode: true,
  },

  credits: {
    enabled: false,
  },

  title: {
    text: '',
  },

  xAxis: {
    crosshair: true,
    tickWidth: 0,
    tickLength: 0,
    labels: {
      formatter(this: AxisLabelsFormatterContextObject) {
        if ((this.chart.options.chart as any).custom?.xAxisDateTimePeriod === Periods.WEEKS) {
          let start = moment.unix(Number(this.value) / 1000);
          let end = start.clone().add(6, 'd');
          return `${start.format('MMM D')} - ${end.format('MMM D')}`;
        }

        return this.axis.defaultLabelFormatter.call(this);
      },
    },
    dateTimeLabelFormats: {
      day: '%b %e',
      week: '%b %e',
    },
  },

  yAxis: {
    title: {
      text: '',
    },
    maxPadding: 0,
    min: 0,
    minRange: 1,
  },

  plotOptions: {
    line: {
      ...MARKER_SETTINGS,
      className: 'highcharts-line-width-3',
    },
    area: {
      ...MARKER_SETTINGS,
    },
    column: {
      groupPadding: 0.1,
      pointPadding: 0,
    },
  },

  legend: {
    layout: 'horizontal',
    align: 'right',
    verticalAlign: 'top',
    symbolWidth: 12,
    symbolHeight: 12,
    symbolRadius: 2,
    useHTML: true,
    labelFormatter(this: Series) {
      return `<span class="highcharts-legend-item-name text-xs cursor-pointer">${this.name}</span>`;
    },
  },

  tooltip: {
    animation: true,
    shared: true,
    useHTML: true,
    borderWidth: 0,
    formatter(
      this: TooltipFormatterContextObject,
      tooltip: any, // this one should ideally be Tooltip, but it seems their type definitions aren't quite complete
      callback: any,
    ) {
      let firstPointConfig = this.points?.[0]?.point.getLabelConfig();
      let header = firstPointConfig ? tooltip.tooltipFooterHeaderFormatter(firstPointConfig) : '';
      // one week
      if (
        (firstPointConfig?.series?.chart?.options.chart as any)?.custom.xAxisDateTimePeriod === Periods.WEEKS &&
        this.x != null &&
        !isNaN(+this.x)
      ) {
        let start = moment.unix((this.x as number) / 1000);
        let end = start.clone().add(6, 'd');
        header = `${start.format(TOOLTIP_WEEK_MOMENT_FORMAT)} - ${end.format(TOOLTIP_WEEK_MOMENT_FORMAT)}`;
      }

      return `
<div class="fly-popover text-xs">
  <div class="fly-popover__content p-1.5">
    <h3 class="${tooltip?.options?.headerClasses ?? ''}">${header}</h3>
    ${tooltip.bodyFormatter(this.points).join('')}
    ${callback ? callback(tooltip) : ''}
  </div>
</div>
`;
    },
    pointFormatter(this: CioHighchartsPoint) {
      let seriesTooltipOptions: SeriesTooltipOptionsObject = (this.series as any).tooltipOptions;
      let valueDecimals = seriesTooltipOptions.valueDecimals ?? '';
      let valuePrefix = seriesTooltipOptions.valuePrefix ?? '';
      let valueSuffix = seriesTooltipOptions.valueSuffix ?? '';

      let value = '';

      if (!isNone(this.name)) {
        value = this.name;
      } else {
        if (this.options.custom?.isNull) {
          value = '--';
        } else {
          // The "y" values here are not formatted. We copy some of the logic from
          // https://github.com/highcharts/highcharts/blob/71d31ebc14172ec0de28912e54a7ae5f2cc2d7d0/ts/parts/Point.ts#L1133
          // to perform the formatting.
          // This happens automatically for "pointFormat", but we want to be able
          // to use a custom "name" (the if condition above), which isn't
          // supported there (so we use "pointFormatter" for more control, but
          // this doesn't format values automatically).
          let key = `${valuePrefix}{point.y:,.${valueDecimals}f}${valueSuffix}`;
          value = window.Highcharts.format(key, { point: this, series: this.series }, this.series.chart);
        }
      }
      return `
<div class="mt-0.5">
  <div class="flex justify-start items-center">
    <div>
      <div class="highcharts-tooltip-symbol highcharts-tooltip-symbol-${(this.series as any).colorIndex}"></div>
    </div>
    <div class="flex-1 max-w-full min-w-0 ml-0.5">
      <div class="flex justify-start">
        <div class="text-truncate">${this.series.name}</div><div>: <b>${value}</b></div>
      </div>
    </div>
  </div>
  ${this.options.custom?.isNull ? `<div class="text-muted pl-[16px] mt-0.5">No data for this period</div>` : ''}
</div>
      `;
    },
    followPointer: true,
    shadow: false,
  },

  exporting: {
    enabled: false,
  },
});
